import { useState, type KeyboardEvent, type ReactElement, type ReactNode } from 'react';
import { ContextMenu as BaseContextMenu } from '@base-ui/react/context-menu';
import { Menu as BaseMenu } from '@base-ui/react/menu';
import { IconChevronRight } from '@tabler/icons-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';

import { cn } from '../../lib/cn';
import { DUR, EASE, PRESS, staggerStep } from '../../interaction';

export interface MenuItem {
  id: string;
  label: string;
  icon?: ReactNode;
  /** Displayed right-aligned. The caller still owns the actual key binding. */
  shortcut?: string;
  disabled?: boolean;
  onSelect?: () => void;
  /**
   * Keeps `label` as the row's accessible name while hiding it visually, for a
   * row whose icon already shows what it is — a flag, a swatch, a single glyph.
   * The name has to survive: an icon alone is unreadable to a screen reader, and
   * the row would announce as nothing.
   */
  hideLabel?: boolean;
  /** Nested items turn this item into a submenu trigger. */
  items?: MenuItem[];
}

export interface MenuSeparator {
  id: string;
  type: 'separator';
}

export type MenuEntry = MenuItem | MenuSeparator;
export type MenuPlacement = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';

function isSeparator(entry: MenuEntry): entry is MenuSeparator {
  return 'type' in entry;
}

const ITEM_CLASS = cn(
  // `dhd-target`: the row is 36px, the hit area is not allowed to be.
  'dhd-target relative flex min-h-9 w-full select-none items-center gap-2.5 rounded-xs px-2.5',
  'font-body text-xs text-body',
  'data-[highlighted]:bg-surface-muted data-[highlighted]:text-heading',
  'data-[disabled]:pointer-events-none data-[disabled]:opacity-45',
  PRESS,
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
);

const SEPARATOR_CLASS = 'my-1 h-px shrink-0 bg-line-subtle';

export interface MenuProps {
  items: MenuEntry[];
  /** Accessible name for the menu surface. */
  label: string;
  /** The element that opens the menu — or, with `contextMenu`, the area it belongs to. */
  trigger: ReactElement;
  /**
   * Right-click mode: the trigger element becomes the area the context menu is
   * invoked on, instead of a button that opens it on press. Make that area
   * focusable (`tabIndex={0}`) — Shift+F10 and the Menu key are bound on it.
   */
  contextMenu?: boolean;
  placement?: MenuPlacement;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Surface width, e.g. `w-64`. */
  className?: string;
}

/**
 * Dropdown and context menu, on Base UI's Menu.
 *
 * Keyboard handling is the library's and it is complete: Up/Down move, Home/End
 * jump to the ends, Enter/Space select, Escape closes the innermost level, and
 * Left/Right (plus Enter/ArrowRight) move between a submenu and its parent. The
 * one binding it does not carry is the context menu's Shift+F10 / Menu key, and
 * that one is ours. Items are portaled, so a parent's `overflow` never clips the
 * surface.
 *
 * What this file adds: the item model, the submenu recursion, the house styling,
 * and a staggered entrance. `AnimatePresence` gates each surface so the exit
 * animation runs and `keepMounted`/`hidden={false}` keep the portaled parts in
 * the DOM while it does.
 */
export function Menu({
  items,
  label,
  trigger,
  contextMenu = false,
  placement = 'bottom-start',
  open,
  defaultOpen = false,
  onOpenChange,
  className,
}: MenuProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  // The context menu's anchor is the POINTER; a keyboard open has none, so for
  // that route only the trigger element stands in as the anchor.
  const [keyboardAnchor, setKeyboardAnchor] = useState<Element | null>(null);
  const isOpen = open ?? uncontrolledOpen;

  function handleOpenChange(next: boolean) {
    setUncontrolledOpen(next);
    onOpenChange?.(next);
  }

  function handleTriggerKeyDown(event: KeyboardEvent<Element>) {
    // Shift+F10 and the dedicated Menu key are the platform routes into a
    // context menu, and Base UI binds neither — the context-menu trigger listens
    // for `contextmenu` and `longPress` only (`ContextMenuTrigger.js`).
    const isContextMenuKey = event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10');
    if (!isContextMenuKey) return;

    event.preventDefault();
    setKeyboardAnchor(event.currentTarget);
    handleOpenChange(true);
  }

  const [side, align] = placement.split('-') as ['bottom' | 'top', 'start' | 'end'];
  // `hidden={false}`: the positioner is a Base UI part, and Base UI hides it as
  // soon as the store says "closed" (`menu/positioner/MenuPositioner.js`). This
  // component keeps it in the DOM for the duration of the exit animation.
  const positionerProps = {
    hidden: false,
    side,
    align,
    sideOffset: 6,
    collisionAvoidance: { side: 'flip' as const, align: 'shift' as const },
  };

  if (contextMenu) {
    return (
      <BaseContextMenu.Root open={isOpen} onOpenChange={handleOpenChange}>
        <BaseContextMenu.Trigger
          render={trigger}
          // The area is a tab stop, so the keyboard route above is reachable
          // without every caller having to know to add one. A caller that passes
          // its own `tabIndex` keeps it.
          tabIndex={0}
          onKeyDown={handleTriggerKeyDown}
          // A pointer open anchors at the cursor; dropping the keyboard anchor
          // here keeps the two routes from leaving each other's anchor behind.
          onContextMenu={() => setKeyboardAnchor(null)}
        />
        <BaseContextMenu.Portal keepMounted>
          <AnimatePresence>
            {isOpen ? (
              <BaseContextMenu.Positioner
                key="menu"
                // `undefined` falls back to the pointer anchor the library keeps
                // (`MenuPositioner.js:82`), whose initial value is a zero-size
                // rect at (0, 0) — the viewport corner, not the trigger.
                anchor={keyboardAnchor ?? undefined}
                className="z-[var(--z-popover)]"
                {...positionerProps}
              >
                <MenuPanel items={items} label={label} className={className} />
              </BaseContextMenu.Positioner>
            ) : null}
          </AnimatePresence>
        </BaseContextMenu.Portal>
      </BaseContextMenu.Root>
    );
  }

  return (
    <BaseMenu.Root open={isOpen} onOpenChange={handleOpenChange}>
      <BaseMenu.Trigger render={trigger} />
      <BaseMenu.Portal keepMounted>
        <AnimatePresence>
          {isOpen ? (
            <BaseMenu.Positioner key="menu" className="z-[var(--z-popover)]" {...positionerProps}>
              <MenuPanel items={items} label={label} className={className} />
            </BaseMenu.Positioner>
          ) : null}
        </AnimatePresence>
      </BaseMenu.Portal>
    </BaseMenu.Root>
  );
}

function MenuPanel({ items, label, className }: { items: MenuEntry[]; label: string; className?: string }) {
  const reduceMotion = useReducedMotion();
  const panelVariants = reduceMotion
    ? { closed: { opacity: 1 }, open: { opacity: 1 } }
    : {
        closed: { opacity: 0, scale: 0.97 },
        open: {
          opacity: 1,
          scale: 1,
          // The per-item cascade is `--stagger-step`, not a second number typed
          // in here; the delay before the first item is the fastest duration
          // token, so the panel has begun to settle before its rows arrive.
          transition: { staggerChildren: staggerStep(), delayChildren: DUR.instant },
        },
      };

  return (
    <BaseMenu.Popup
      role="menu"
      aria-label={label}
      data-focus-ring="none"
      className={cn(
        'min-w-56 rounded-xs border-2 border-line-strong bg-surface p-1 shadow-hard',
        className,
      )}
      render={
        <motion.div
          variants={panelVariants}
          initial="closed"
          animate="open"
          exit={reduceMotion ? undefined : { opacity: 0, scale: 0.98 }}
          transition={reduceMotion ? { duration: 0 } : { duration: DUR.fast, ease: EASE.out }}
        />
      }
    >
      <MenuEntries items={items} />
    </BaseMenu.Popup>
  );
}

type ItemVariants = Record<'closed' | 'open', { opacity: number; x?: number }>;

function MenuEntries({ items }: { items: MenuEntry[] }) {
  const reduceMotion = useReducedMotion();
  const itemVariants: ItemVariants = reduceMotion
    ? { closed: { opacity: 1 }, open: { opacity: 1 } }
    : { closed: { opacity: 0, x: -6 }, open: { opacity: 1, x: 0 } };

  return (
    <>
      {items.map((entry) =>
        isSeparator(entry) ? (
          <BaseMenu.Separator key={entry.id} className={SEPARATOR_CLASS} />
        ) : entry.items && entry.items.length > 0 ? (
          <Submenu key={entry.id} item={entry} itemVariants={itemVariants} />
        ) : (
          <BaseMenu.Item
            key={entry.id}
            disabled={entry.disabled}
            onClick={entry.onSelect}
            className={ITEM_CLASS}
            render={<motion.div variants={itemVariants} />}
          >
            <MenuRow item={entry} />
          </BaseMenu.Item>
        ),
      )}
    </>
  );
}

function Submenu({
  item,
  itemVariants,
}: {
  item: MenuItem;
  itemVariants: ItemVariants;
}) {
  const [open, setOpen] = useState(false);

  return (
    <BaseMenu.SubmenuRoot open={open} onOpenChange={setOpen}>
      <BaseMenu.SubmenuTrigger
        disabled={item.disabled}
        className={cn(ITEM_CLASS, 'data-[popup-open]:bg-surface-muted')}
        render={<motion.div variants={itemVariants} />}
      >
        <MenuRow item={item} />
        <IconChevronRight className="ms-auto size-4 shrink-0 text-muted" aria-hidden="true" />
      </BaseMenu.SubmenuTrigger>
      <BaseMenu.Portal keepMounted>
        <AnimatePresence>
          {open ? (
            <BaseMenu.Positioner
              key="submenu"
              hidden={false}
              side="inline-end"
              align="start"
              sideOffset={4}
              className="z-[var(--z-popover)]"
            >
              <MenuPanel items={item.items ?? []} label={item.label} />
            </BaseMenu.Positioner>
          ) : null}
        </AnimatePresence>
      </BaseMenu.Portal>
    </BaseMenu.SubmenuRoot>
  );
}

function MenuRow({ item }: { item: MenuItem }) {
  return (
    <>
      {item.icon ? (
        <span className="grid size-4 shrink-0 place-items-center text-muted" aria-hidden="true">
          {item.icon}
        </span>
      ) : null}
      {item.hideLabel ? (
        <span className="dhd-sr-only">{item.label}</span>
      ) : (
        <span className="flex-1 truncate text-left">{item.label}</span>
      )}
      {item.shortcut ? (
        // On a hidden-label row the shortcut is a STATE marker rather than a key
        // binding — the language list's tick, which says which option is on. It
        // is hidden from the accessibility tree because the `kbd` is not one of
        // the roles the name computation skips: left visible to it, the row would
        // announce as "Português (Brasil) ✓", with the state read out as a stray
        // character after the language's own name.
        <kbd
          aria-hidden={item.hideLabel ? true : undefined}
          className="ms-3 shrink-0 font-mono text-2xs tracking-wider text-muted"
        >
          {item.shortcut}
        </kbd>
      ) : null}
    </>
  );
}
